import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Keyboard, PanResponder, View } from 'react-native';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    H6Emphasized,
    isWindowDefined,
    KeyboardCompatibleBottomSheet,
    YStack,
} from 'smoothie';
import { isEmpty } from 'smoothie/src/misc';
import { useDebouncedCallback } from 'use-debounce';

import { useLogout } from '../../auth/hooks/useLogout';
import {
    sessionTimeoutAtom,
    sessionTimeoutWarningAtom,
} from '../../auth/state/AuthAtoms';
import { LogoutType } from '../../auth/types/Auth';
import { StorageKeys } from '../../common/constants';
import { useStorage } from '../../common/providers/StorageProvider';
import type { ContainerComponent } from '../../common/types/Common';
import { INACTIVITY_DURATION } from '../../config/config';
import { isQuilttConnectorOpenAtom } from '../../holistic/state/HolisticAtoms';

const TIMEOUT_MS = INACTIVITY_DURATION ?? 600000; // Time out after TIMEOUT_MS (or 10 minutes) of no activity
const TIMEOUT_WARNING_MS =
    TIMEOUT_MS >= 600000 ? TIMEOUT_MS - 120000 : TIMEOUT_MS * 0.8; // Show time out warning 2 minutes (or 80% of TIMEOUT_MS) before timeout

interface TimeoutResetOptions {
    skipRecordingActivity?: boolean;
}

// This component watches for touch events from PanResponder and other interactions
// If it doesn't detect any within the TIMEOUT_WARNING_MS threshold, it will show a warning
// If it doesn't detect any within the TIMEOUT_MS threshold (total), it will log the user out
const InactivityDetector: ContainerComponent = ({ children }) => {
    const { logOut } = useLogout();
    const storage = useStorage();
    const setLogoutTimer = useSetAtom(sessionTimeoutAtom);
    const setWarningTimer = useSetAtom(sessionTimeoutWarningAtom);
    const [isTimeoutWarningVisible, setIsTimeoutWarningVisible] =
        useState(false);
    const hasExtendedTimeoutDueToKeyboardVisible = useRef(false);
    const hasExtendedTimeoutDueToQuiltt = useRef(false);
    const timeoutWarningSheetRef = useRef<BottomSheetModal>(null);
    const sessionHasTimedOut = useRef(false);
    const [showSpinner, setShowSpinner] = useState(false);

    const isQuilttConnectorOpen = useAtomValue(isQuilttConnectorOpenAtom);
    const isQuilttConnectorOpenRef = useRef(isQuilttConnectorOpen);

    // This chunk allows for granting an extension to the timeout for exceptional cases
    const checkTimeoutExtensionGrant = useCallback(() => {
        // In React Native, we can't globally listen for key press events to reset the inactivity timer
        // (without the overhead of additional plugins). This leads to unexpected timeouts on the messaging page
        // due to undetected typing activity. As a workaround, we extend the timeout once when the keyboard is visible.
        if (
            Keyboard.isVisible?.() &&
            !hasExtendedTimeoutDueToKeyboardVisible.current
        ) {
            hasExtendedTimeoutDueToKeyboardVisible.current = true;
            resetInactivityTimeout();
            return true;
        }

        if (
            isQuilttConnectorOpenRef.current &&
            !hasExtendedTimeoutDueToQuiltt.current
        ) {
            hasExtendedTimeoutDueToQuiltt.current = true;
            resetInactivityTimeout();
            return true;
        }

        return false;
    }, []);

    const resetTimeoutExtensionGrant = useCallback(() => {
        if (!Keyboard.isVisible?.()) {
            hasExtendedTimeoutDueToKeyboardVisible.current = false;
        }
        if (!isQuilttConnectorOpenRef.current) {
            hasExtendedTimeoutDueToQuiltt.current = false;
        }
    }, []);

    const hideTimeoutWarning = useCallback(() => {
        timeoutWarningSheetRef.current?.forceClose();
        timeoutWarningSheetRef.current?.dismiss();
        setIsTimeoutWarningVisible(false);
    }, []);

    const resetLogoutTimer = useCallback(
        (newTimer?: number | NodeJS.Timeout) => {
            setLogoutTimer((prevTimer) => {
                if (prevTimer != null) {
                    clearTimeout(prevTimer);
                }
                return newTimer;
            });
        },
        [],
    );
    const resetWarningTimer = useCallback(
        (newTimer?: number | NodeJS.Timeout) => {
            setWarningTimer((prevTimer) => {
                if (prevTimer != null) {
                    clearTimeout(prevTimer);
                }
                return newTimer;
            });
        },
        [],
    );

    const resetInactivityTimeout = useCallback(
        (options: TimeoutResetOptions = {}) => {
            hideTimeoutWarning();

            if (!sessionHasTimedOut.current) {
                if (!options.skipRecordingActivity) {
                    storage.setString(
                        StorageKeys.LATEST_ACTIVITY_TIMESTAMP,
                        Date.now().toString(),
                    );
                }

                resetWarningTimer(
                    setTimeout(() => {
                        if (
                            !sessionHasTimedOut.current &&
                            !checkTimeoutExtensionGrant()
                        ) {
                            Keyboard.dismiss();
                            setIsTimeoutWarningVisible(true);
                        }
                    }, TIMEOUT_WARNING_MS),
                );

                resetLogoutTimer(
                    setTimeout(() => {
                        if (!sessionHasTimedOut.current) {
                            sessionHasTimedOut.current = true;
                            hideTimeoutWarning();
                            setShowSpinner(true);
                            void logOut(LogoutType.Inactivity);
                        }
                    }, TIMEOUT_MS),
                );

                resetTimeoutExtensionGrant();
            }
        },
        [],
    );

    const debouncedResetInactivityTimeout = useDebouncedCallback(
        () => {
            // wrapping this function to avoid typing conflicts with event handlers
            // and delaying with setTimeout to avoid race conditions with the session timeout handler
            // this is necessary because when the native app is in the background, the session timeout handler
            // won't execute until the app is brought back to the foreground. If the timeout period was reached
            // while in the background, we want that to be handled before this would reset the timeout.
            setTimeout(resetInactivityTimeout, 500);
        },
        2000,
        { leading: true },
    );

    useEffect(() => {
        debouncedResetInactivityTimeout();
        return () => {
            hideTimeoutWarning();
            resetLogoutTimer();
            resetWarningTimer();
        };
    }, []);

    // 1. Detect activity via touch events
    // This works well on web and mobile web, firing an event when 'click' and 'touchstart' events would
    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponderCapture: () => {
                debouncedResetInactivityTimeout();
                return false;
            },
        }),
    ).current;

    // 2. Detect activity via keyboard showing
    useEffect(() => {
        const keyboardShowSubscription = Keyboard.addListener(
            'keyboardDidShow',
            debouncedResetInactivityTimeout,
        );
        return () => {
            keyboardShowSubscription.remove();
        };
    }, []);

    // 3. Detect activity via AppState changing to active
    useEffect(() => {
        const appStateSubscription = AppState.addEventListener('change', () => {
            // Delaying this handler to force it to run after any timeout handlers
            // so that we can get an accurate reading of the app state
            setTimeout((nextAppState) => {
                // Opting to not reset the timer if the timeout warning is visible for 2 reasons:
                // 1. When the app becomes active again, the UX feels a bit weird to see the warning show and then disappear before you can read it.
                // 2. For an unknown reason, if the sheet is shown and then immediately dismissed by code, the app will become unresponsive (similar to reanimated issues).
                if (nextAppState === 'active' && !isTimeoutWarningVisible) {
                    debouncedResetInactivityTimeout();
                }
            }, 500);
        });

        return () => {
            appStateSubscription.remove();
        };
    }, []);

    // 4. Detect web activity via window events
    useEffect(() => {
        const webActivityEvents = ['load', 'keydown', 'scroll'];
        if (isWindowDefined) {
            webActivityEvents.forEach((event) => {
                window.addEventListener(
                    event,
                    debouncedResetInactivityTimeout,
                    true,
                );
            });
        }

        return () => {
            if (isWindowDefined) {
                webActivityEvents.forEach((event) => {
                    window.removeEventListener(
                        event,
                        debouncedResetInactivityTimeout,
                        true,
                    );
                });
            }
        };
    }, []);

    // 5. Detect web activity in other tabs
    useEffect(() => {
        const handleStorageEvent = (event: StorageEvent) => {
            if (
                event.key === StorageKeys.LATEST_ACTIVITY_TIMESTAMP &&
                !isEmpty(event.newValue)
            ) {
                resetInactivityTimeout({ skipRecordingActivity: true });
            }
        };
        if (isWindowDefined) {
            window.addEventListener('storage', handleStorageEvent, true);
        }

        return () => {
            if (isWindowDefined) {
                window.removeEventListener('storage', handleStorageEvent, true);
            }
        };
    }, []);

    // 6. Detect activity via Quiltt Connector
    useEffect(() => {
        isQuilttConnectorOpenRef.current = isQuilttConnectorOpen;
    }, [isQuilttConnectorOpen]);

    if (showSpinner) {
        return <FullScreenSpinner />;
    }

    return (
        <View style={{ flex: 1 }} {...panResponder.panHandlers}>
            {children}
            {isTimeoutWarningVisible && (
                <KeyboardCompatibleBottomSheet
                    sheetRef={timeoutWarningSheetRef}
                    onDismiss={() => {}}
                    showCloseButton={false}
                    onPressBackdrop={resetInactivityTimeout}
                    enablePanDownToClose={false}
                    isInsideModal={true} // Enables this sheet to show over native 'modal' screens
                    // On mobile web, there are a few modal-like screens that this sheet shows behind
                    // To resolve that, we may need this component to use the Tamagui Sheet on mobile web
                >
                    <YStack gap={12} marginHorizontal="auto">
                        <H6Emphasized>Are you still there?</H6Emphasized>
                        <BodySmallRegular>
                            If you&apos;re away, we&apos;ll sign you out to keep
                            your information secure.
                        </BodySmallRegular>
                        <YStack marginTop={24} $sm={{ marginTop: 48 }}>
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.LARGE}
                                text="Yes, keep me signed in"
                                onPress={() => {
                                    timeoutWarningSheetRef.current?.dismiss();
                                    resetInactivityTimeout();
                                }}
                            />
                        </YStack>
                    </YStack>
                </KeyboardCompatibleBottomSheet>
            )}
        </View>
    );
};

export default InactivityDetector;
